// A FEJLESZTÉS-KÁRTYA — a board és az idővonal KÖZÖS eleme a belső lapon.
//
// MIÉRT EGY KOMPONENS KETTŐRE: a board és az idővonal ugyanazt a tételt mutatja, csak más rendezésben. Két
// külön kártyából a jelvények, a tipográfia és a kattintás-viselkedés elkerülhetetlenül elcsúszna — és a
// felhasználó ugyanarra az adatra két különböző képet látna nézet-váltáskor.
//
// A KÁRTYA MAGA KATTINTHATÓ (szerkesztés), de csak akkor, ha a hívó ad `onOpen`-t: olvasó jognál a kártya
// nem tesz úgy, mintha lehetne rajta cselekedni. A sor-akciók a LÁBLÉCBEN élnek, az idő-sor jobb oldalán, és
// a kattintásuk NEM buborékol tovább — különben minden akció a szerkesztőt is megnyitná.
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductUpdateTypeBadge } from '@/components/product-updates/product-update-type-badge';
import { DomainBadge } from '@/components/shared/domain-badge';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

export interface ProductUpdateCardItem {
  id: string;
  type: string;
  status: string;
  visibility: string;
  stage: string;
}

export interface ProductUpdateCardProps {
  item: ProductUpdateCardItem;
  title: string;
  /** Az IDŐ-sor: a boardon a horizont/cél-időszak, az idővonalon a kiadás dátuma. A lábléc bal oldalán áll. */
  meta?: ReactNode;
  /** Szerkesztés-megnyitás; hiányában a kártya nem kattintható (olvasó jog). */
  onOpen?: (() => void) | undefined;
  /** Sor-akciók (kihirdetés/szerkesztés/törlés) — a jobb felső sarokban. */
  actions?: ReactNode;
  className?: string;
}

export function ProductUpdateCard({
  item,
  title,
  meta,
  onOpen,
  actions,
  className,
}: ProductUpdateCardProps): JSX.Element {
  const { t } = useTranslation();
  const interactive = onOpen !== undefined;
  return (
    <div
      className={cn(
        'border-border card-glass group rounded-lg border p-3',
        interactive &&
          'hover:border-accent-500/50 focus-within:border-accent-500/50 cursor-pointer transition-colors duration-150',
        className,
      )}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: onOpen,
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            },
          }
        : {})}
    >
      <div className="min-w-0 space-y-1.5">
        {/* A CÍM a kártya TELJES szélességét kapja. Az akciók se rá abszolút pozícionálva (a gombok száma
            jogfüggő, 1–3 — egy fix jobb-padding vagy kevés, és a cím alácsúszik, vagy fölösleges helyet
            hagy), se mellette egy flex-sorban (a board keskeny oszlopában a cím négy sorba tört): a
            LÁBLÉCBEN élnek, az idő-sor jobb oldalán. Élő nézeten mindkét korábbi alak megbukott. */}
        <Text variant="itemTitle" as="div" className="min-w-0 break-words">
          {title}
        </Text>
        <div className="flex flex-wrap items-center gap-1.5">
          <ProductUpdateTypeBadge type={item.type} label={t(`productUpdates.type.${item.type}`)} />
          {/* A PUBLIKUS láthatóság a kivétel (a tételek kétharmada belső), ezért CSAK azt jelöljük —
              a „belső" jelvény minden kártyán zaj lenne. */}
          {item.visibility === 'public' && (
            <DomainBadge
              kind="productUpdateVisibility"
              value="public"
              label={t('productUpdates.visibility.public')}
            />
          )}
          {item.status === 'draft' && (
            <DomainBadge
              kind="productUpdateStatus"
              value="draft"
              label={t('productUpdates.status.draft')}
            />
          )}
        </div>
        {(meta !== undefined || actions !== undefined) && (
          <div className="flex min-h-7 items-center justify-between gap-2">
            <Text tone="tertiary" as="div" className="min-w-0 truncate text-xs">
              {meta}
            </Text>
            {actions !== undefined && (
              // A `stopPropagation` NEM óvatoskodás: enélkül minden sor-akció a kártya `onClick`-jét is
              // elsütné, tehát a törlés-gomb a szerkesztőt is megnyitná mögötte.
              <div
                className="flex shrink-0 items-center"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="presentation"
              >
                {actions}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
